import { createContext, useCallback, useContext, useRef } from "react";

// Todo: Filter out functions from data being returned to analytics
const AnalyticsContext = createContext([]);

const useAnalyticsData = (namespace, initial) => {
	const context = useContext(AnalyticsContext);
	let _tree = context || [];
	const lastEvent = _tree.length > 0 ? _tree[_tree.length - 1] : {};
	const isVirtualEvent =
		namespace !== "" && lastEvent?._component !== namespace;
	if (typeof initial !== "undefined") {
		context[context.length - 1]._state = {
			...(context[context.length - 1]._state || {}),
			...(initial || {}),
		};
	}
	const flat = context.reduce(
		(acc, { _state, _props, ...inc }) => ({
			...acc,
			..._props,
			..._state,
			...inc,
		}),
		{},
	);

	return (action, props = {}) => {
		const eventName = `${namespace && namespace + "/"}${action}`;
		if (isVirtualEvent) {
			// every event needs an entry in the tree, even if the component is not wrapped by analytics
			const prev = _tree.at(-1);
			if (prev && prev._component === namespace) {
				_tree[_tree.length - 1] = {
					_component: namespace,
					_props: props,
					_state: initial,
					_isVirtual: true,
				};
			} else {
				_tree = [
					..._tree,
					{
						_component: namespace,
						_props: props,
						_state: initial,
						_isVirtual: true,
					},
				];
			}
		}

		if (action.indexOf("/") !== -1) {
			_tree = [
				..._tree,
				{
					_component: action.split("/")[0],
					_props: props,
					_state: initial,
				},
			];
		}

		const resolved = flat?.useOnFireAnalytics?.() || {};

		const data = {
			_tree,
			...flat,
			...props,
			...resolved,
		};

		return [eventName, data];
	};
};

function replaceBracesContent(str, obj) {
	return str.replace(/{([^}]+)}/g, (match, key) => {
		return obj.hasOwnProperty(key)
			? encodeURIComponent(obj[key].replaceAll(" ", ""))
			: match;
	});
}

/**
 *
 * Usage:
 * const withIntsrcSearchParams = useAnaltyicsQueryParam({intsrc: "{brand}:{section}:{action}:{destination}"});
 * const output = withIntsrcSearchParams(url, "dealervisit", { destination: "" })
 *
 */
const DEFAULT_ACTION = "dealervisit";
export const useAnalyticsQueryParam = (query, initial) => {
	const getData = useAnalyticsData("", initial);

	return (url, props) => {
		// Handle empty or invalid URLs
		if (!url) return url;

		const [, data] = getData(props?.action ?? DEFAULT_ACTION, props);
		const isToyota =
			data?.app?.toLowerCase() === "t" ||
			data?.app?.toLowerCase() === "tcom" ||
			data?.theme?.toLowerCase() === "toyota";
		const brand = isToyota ? "tcom" : "lcom";
		const dataWithBrand = {
			action: DEFAULT_ACTION,
			...data,
			brand,
		};

		try {
			// Handle relative URLs by using current origin
			const baseUrl = url.startsWith("http")
				? url
				: `${window.location.origin}${url}`;
			const hrefURL = new URL(baseUrl);

			if (props?.dealerPageType) {
				hrefURL.searchParams.set("dealerPageType", props.dealerPageType);
			}

			for (let k in query) {
				const value = query[k];
				hrefURL.searchParams.set(k, replaceBracesContent(value, dataWithBrand));
			}

			// For relative URLs, return just the pathname + search
			if (!url.startsWith("http")) {
				return `${hrefURL.pathname}${hrefURL.search}`;
			}

			return hrefURL.toString().replace(/%3A/g, ":");
		} catch (e) {
			console.warn("[Analytics URL Error]", e);
			return url; // Return original URL if parsing fails
		}
	};
};

export const useAnalytics = (namespace, initial = {}) => {
	const getData = useAnalyticsData(namespace, initial);

	return (action, props = {}) => {
		const [eventName, data] = getData(action, props);

		try {
			if (
				window.top !== window.self &&
				typeof window.parent.dispatchEvent === "function"
			) {
				window.parent.dispatchEvent(
					new CustomEvent("LEXUS_ANALYTICS_EVENT", {
						detail: {
							eventName,
							data: {
								...data,
								origin: window.location.href,
								isIframe: true,
							},
						},
					}),
				);
			}
			window.dispatchEvent(
				new CustomEvent("LEXUS_ANALYTICS_EVENT", {
					detail: {
						eventName,
						data: {
							...data,
							origin: window.location.href,
							isIframe: false,
						},
					},
				}),
			);
		} catch (e) {
			console.log("[Analytics Error]", e);
		}
	};
};

export const AnalyticsProvider = ({
	data: { _component, ...data } = {},
	children,
}) => {
	const context = useContext(AnalyticsContext);
	const state = useRef({});
	const dataWithRef = {
		_component,
		_props: data,
		_state: state.current,
	};
	const tree = [...context, dataWithRef];

	const treeRef = useRef();
	treeRef.current = tree;

	return (
		<AnalyticsContext.Provider value={treeRef.current}>
			{children}
		</AnalyticsContext.Provider>
	);
};

export const withAnalytics =
	(Component) =>
	(analytics = {}) => {
		const _component = (
			analytics._component ||
			Component?.displayName ||
			Component.name
		)
			?.split("$")
			.at(0);

		const ComponentWithAnalytics = ({ ...props }) => {
			const data = {
				_component,
				...analytics,
				...props,
			};

			return (
				<AnalyticsProvider data={data}>
					<Component {...props} />
				</AnalyticsProvider>
			);
		};
		if (_component) {
			ComponentWithAnalytics.displayName = `${_component}WithAnalytics`;
		}

		return ComponentWithAnalytics;
	};

export const initAnalyticsListener = (handler = () => {}) => {
	window.addEventListener("LEXUS_ANALYTICS_EVENT", handler, false);
};

export default useAnalytics;
